'use client';

import { useState, FormEvent } from 'react';
import { trackLead } from '@/lib/tracking';

// SVG Icons
const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14"/>
    <path d="m12 5 7 7-7 7"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const LoadingSpinner = () => (
  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
  consent?: string;
}

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false,
  });
  const [honeypot, setHoneypot] = useState('');
  const [formLoadedAt] = useState(Date.now());
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateField = (name: string, value: string | boolean): string | undefined => {
    switch (name) {
      case 'name':
        if (!value || (typeof value === 'string' && value.trim().length < 2)) {
          return 'Jméno musí mít alespoň 2 znaky';
        }
        break;
      case 'email':
        if (!value || (typeof value === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) {
          return 'Zadejte platný e-mail';
        }
        break;
      case 'message':
        if (!value || (typeof value === 'string' && value.trim().length < 10)) {
          return 'Zpráva musí mít alespoň 10 znaků';
        }
        break;
      case 'consent':
        if (!value) {
          return 'Musíte souhlasit se zpracováním údajů';
        }
        break;
    }
    return undefined;
  };

  const handleBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    (Object.keys(formData) as Array<keyof typeof formData>).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key as keyof FormErrors] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, message: true, consent: true });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          message: formData.message,
          source: 'contact-page',
          website: honeypot,
          _t: formLoadedAt,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackLead({ source: 'contact-page', name: formData.name, email: formData.email });
        setFormData({ name: '', email: '', phone: '', message: '', consent: false });
        setTouched({});
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  return (
    <section className="contact-page">
      <div className="contact-container">
        {/* Header */}
        <div className="contact-header">
          <h1>Kontakt</h1>
          <p>Máte zájem o spolupráci? Neváhejte mě kontaktovat.</p>
        </div>

        {/* Contact Info Boxes - 3 in a row */}
        <div className="contact-boxes">
          <div className="contact-box">
            <div className="contact-box-icon">
              <MailIcon />
            </div>
            <div className="contact-box-content">
              <h3>E-mail</h3>
              <a href="mailto:hi@jakubmatuska.cz">hi@jakubmatuska.cz</a>
            </div>
          </div>

          <div className="contact-box">
            <div className="contact-box-icon">
              <PhoneIcon />
            </div>
            <div className="contact-box-content">
              <h3>Telefon</h3>
              <a href="tel:+420774801300">+420 774 801 300</a>
            </div>
          </div>

          <div className="contact-box">
            <div className="contact-box-icon">
              <ClockIcon />
            </div>
            <div className="contact-box-content">
              <h3>Doba odpovědi</h3>
              <span>Obvykle do 24 hodin</span>
            </div>
          </div>
        </div>

        {/* Billing Info */}
        <div className="billing-section">
          <h3>Fakturační údaje</h3>
          <div className="billing-content">
            <p className="billing-name">Jakub Matuška</p>
            <p>Doly 417, Dobruška 518 01</p>
            <p><strong>Bankovní účet:</strong> 2977243013/0800</p>
            <p><strong>IČ:</strong> 06006124</p>
            <p className="billing-note">Nejsem plátce DPH</p>
          </div>
        </div>

        {/* Contact Form - Full Width */}
        <div className="contact-form-section">
          {submitStatus === 'success' ? (
            <div className="form-success">
              <div className="success-check">
                <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="26" cy="26" r="25" stroke="#4001FF" strokeWidth="2"/>
                  <path d="M15 27l7 7 15-15" stroke="#4001FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2>Děkuji za zprávu!</h2>
              <p>Ozvu se Vám co nejdříve, obvykle do 24 hodin.</p>
              <button className="nav-cta" onClick={() => setSubmitStatus('idle')}>
                Odeslat další zprávu
              </button>
            </div>
          ) : (
            <>
              <h2>Napište mi</h2>
              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} style={{ position: 'absolute', left: '-9999px', tabIndex: -1 } as React.CSSProperties} autoComplete="off" aria-hidden="true" />
                {/* Row: Name, Email, Phone */}
                <div className="form-row">
                  <div className={`form-group ${errors.name && touched.name ? 'has-error' : ''}`}>
                    <label htmlFor="name">Jméno a příjmení *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Jan Novák"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                    />
                    {errors.name && touched.name && (
                      <span className="field-error">{errors.name}</span>
                    )}
                  </div>

                  <div className={`form-group ${errors.email && touched.email ? 'has-error' : ''}`}>
                    <label htmlFor="email">E-mail *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="jan@example.cz"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                    />
                    {errors.email && touched.email && (
                      <span className="field-error">{errors.email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Telefon <span className="optional">(nepovinné)</span></label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="+420 777 888 999"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                    />
                  </div>
                </div>

                {/* Message - Full Width */}
                <div className={`form-group ${errors.message && touched.message ? 'has-error' : ''}`}>
                  <label htmlFor="message">Zpráva *</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Popište svůj projekt, co potřebujete, jaký máte rozpočet..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    onBlur={() => handleBlur('message')}
                    rows={6}
                  />
                  {errors.message && touched.message && (
                    <span className="field-error">{errors.message}</span>
                  )}
                </div>

                {/* Consent + Button Row */}
                <div className="form-footer">
                  <div className={`form-check ${errors.consent && touched.consent ? 'has-error' : ''}`}>
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) => handleChange('consent', e.target.checked)}
                        onBlur={() => handleBlur('consent')}
                      />
                      <span className="checkbox-mark"></span>
                      <span className="checkbox-text">
                        Souhlasím se zpracováním osobních údajů *
                      </span>
                    </label>
                    {errors.consent && touched.consent && (
                      <span className="field-error">{errors.consent}</span>
                    )}
                  </div>

                  <button className="contact-btn" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <LoadingSpinner />
                        <span>Odesílám...</span>
                      </>
                    ) : (
                      <>
                        <span>Odeslat zprávu</span>
                        <div className="btn-icon">
                          <ArrowRightIcon />
                        </div>
                      </>
                    )}
                  </button>
                </div>

                {submitStatus === 'error' && (
                  <div className="form-error">
                    <p>Něco se pokazilo. Zkuste to prosím znovu.</p>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
